/**
 * Empty an html node
 * @param {Node} node html node
 */
function emptyNode(node) {
    while (node && node.firstChild) {
        node.removeChild(node.firstChild);
    }
}

/**
 * Empty an html node and add html
 * @param {Node} node html node
 * @param {string} value html text to add
 */
function replaceHtml(node, value) {
    if (!node) {
        return;
    }
    emptyNode(node);
    // eslint-disable-next-line no-param-reassign
    node.innerHTML = value;
}

/**
 * Remove an html node
 * @param {Node} node html node
 */
function removeNode(node) {
    if (!node) {
        return;
    }
    node.remove();
}

/**
 * Trigger an event
 * @param {string} selector node selector
 * @param {string} eventName event name to trigger
 * @param {Object} data data to pass
 */
function triggerEvent(selector, eventName, data) {
    var node = document.querySelector(selector);
    if (!node) {
        return;
    }
    var event;
    if (data) {
        event = new Event(eventName, data);
    } else {
        event = new Event(eventName);
    }
    node.dispatchEvent(event);
}

/**
 * Empty an html node
 * @param {Node} node html node
 * @param {string} eventName event name to trigger
 * @param {Function} callBack function to execute on event triggered
 */
function onEvent(node, eventName, callBack) {
    if (!node) {
        return;
    }
    node.addEventListener(eventName, callBack);
}

/**
 * Tranform text to an HTML node
 * @param {string} str text to transform to HTML node
 * @return {Node} The resulted node
 */
function stringToHTML(str) {
    var support = (function () {
        if (!window.DOMParser) return false;
        var parser = new DOMParser();
        try {
            parser.parseFromString('x', 'text/html');
        } catch (err) {
            return false;
        }
        return true;
    }());

    // If DOMParser is supported, use it
    if (support) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(str, 'text/html');
        return doc;
    }

    // Otherwise, fallback to old-school method
    var dom = document.createElement('div');
    dom.innerHTML = str;
    return dom;
}

/**
 * Add a class to an element
 * @param {Element} elem element with class to add
 * @param {string} className Class to add
 */
function addClass(elem, className) {
    if (!elem) {
        return;
    }
    elem.classList.add(className);
}

/**
 * Remove a class from an element
 * @param {Element} elem element with class to remove
 * @param {string} className Class to remove
 */
function removeClass(elem, className) {
    if (!elem) {
        return;
    }
    elem.classList.remove(className);
}

/**
 * Return all focusable elements contains in a given element
 * @param {Element} elem element containing the focusable elements
 * @returns {NodeList} list of node that are focusable
 */
function getFocusableElements(elem) {
    return elem.querySelectorAll('button, a, textarea, input, select');
}

/**
 * Set tabindex value to a list of elements
 * @param {Element[]} elements list of elements
 * @param {*} value tabindex value to set on the elements
 */
function setTabindex(elements, value) {
    elements.forEach((element) => {
        element.setAttribute('tabindex', value);
    });
}

/**
 * Skip a number of animation frame before calling callback
 * @param {Function} callback The function to call when it's time to update your animation for the next repaint.
 * @param {number} skipFrameNb Number of animation frames to skip before calling callback
 */
function requestNextAnimationFrame(callback, skipFrameNb) {
    let i = 0;
    const loopFn = () => {
        if (i < skipFrameNb) {
            ++i;
            window.requestAnimationFrame(loopFn);
            return;
        }
        window.requestAnimationFrame(callback);
    };
    window.requestAnimationFrame(loopFn);
}

/**
 * Get dataset property of an element
 * @param {Element} elem Element to pull dataset from
 * @param {string} propertyName Name/key of the dataset property
 * @param {string} type The value type of the property wanted. Will default to defaultValue if its a different type.
 *                      Supported types: boolean, number, string
 * @param {*} [defaultValue] Default value returned if property has a wrong type or doesn't exists
 * @return {*} Value of the property with correct type. Default value if incorrect type or property not present
 */
function getDataProperty(elem, propertyName, type = 'string', defaultValue = undefined) {
    if (!(propertyName in elem.dataset)) return defaultValue;

    let value = elem.dataset[propertyName];
    if (type === 'boolean') {
        value = value.toLowerCase().trim();
        if (value === 'yes' || value === 'true') return true;
        if (value === 'no' || value === 'false') return false;
    } else if (type === 'number') {
        return parseFloat(value);
    } else if (type === 'string') {
        return value;
    }
    return defaultValue;
}

/**
 * Get a DOMRect object providing information about the size of an element
 * and its position relative to the body.
 * @param {Element} elem element to get information from
 * @returns {DOMRect} rectangle which contains the entire element, including its padding and border-width.
 */
function getBoundingPageRect(elem) {
    const rect = elem.getBoundingClientRect();
    // eslint-disable-next-line no-undef
    return new DOMRect(rect.x + window.scrollX, rect.y + window.scrollY, rect.width, rect.height);
}

// Local variable used by "generateIdAttribute()"
const ElementsIdCounts = {};

/**
 * Generate a unique ID attribut and avoid conflict with existing element IDs
 * @param {string} idPrefix Prefix of the ID attribut
 * @returns {string} Unique ID attribut
 */
function generateIdAttribute(idPrefix) {
    let id;
    do {
        const count = (ElementsIdCounts[idPrefix] || 0) + 1;
        ElementsIdCounts[idPrefix] = count;
        id = `${idPrefix}-${count}`;
    // Avoid ID conflict. Will retry on conflict
    } while (document.getElementById(id));
    return id;
}

module.exports = {
    emptyNode,
    replaceHtml,
    removeNode,
    triggerEvent,
    onEvent,
    stringToHTML,
    addClass,
    removeClass,
    getFocusableElements,
    setTabindex,
    requestNextAnimationFrame,
    getDataProperty,
    getBoundingPageRect,
    generateIdAttribute,
};
